/**
 * API service module for the Config Generator page.
 *
 * Provides a standalone fetch-based service with 3 endpoints:
 *   - prePopulate  — calls the LLM pre-populator
 *   - generateConfig — serialises form state into YAML
 *   - saveConfig — persists YAML to S3
 *
 * Uses `getValidToken()` for auth (same pattern as `store/api.ts`).
 * Throws `ConfigGeneratorApiError` for non-2xx responses.
 */

import { getValidToken } from 'src/auth/auth';
import { CONFIG_GENERATOR_ENDPOINT } from 'src/constants';
import {
  ConfigGeneratorApiError,
  GenerateConfigRequest,
  GenerateConfigResponse,
  PrePopulateRequest,
  PrePopulatedConfigResponse,
  SaveConfigRequest,
  SaveConfigResponse,
} from './types';

// ─── Endpoint Paths ────────────────────────────────────────────────────

const ENDPOINTS = {
  PRE_POPULATE: '/api/config-generator/pre-populate',
  GENERATE: '/api/config-generator/generate',
  SAVE: '/api/config-generator/save',
} as const;

// ─── Internal Helpers ──────────────────────────────────────────────────

async function apiRequest<TResponse>(
  endpoint: string,
  body: unknown,
  baseUrl?: string,
): Promise<TResponse> {
  const base = baseUrl ?? CONFIG_GENERATOR_ENDPOINT;
  const url = `${base}${endpoint}`;

  let response: Response;
  try {
    const token = await getValidToken();
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    // fetch throws TypeError on network failures (DNS, connection refused, etc.)
    const message =
      networkError instanceof Error
        ? networkError.message
        : 'Network error — check your connection';
    throw new ConfigGeneratorApiError(0, message);
  }

  if (!response.ok) {
    let detail: string;
    try {
      const errorBody = await response.json();
      detail = errorBody.detail ?? errorBody.message ?? response.statusText;
    } catch (_parseError) {
      detail = response.statusText;
    }
    throw new ConfigGeneratorApiError(response.status, detail);
  }

  return response.json() as Promise<TResponse>;
}

// ─── Public API (3 endpoints only — no parseSchema) ────────────────────

/**
 * Call the LLM pre-populator to generate config field values from
 * schema content, business knowledge, and sample questions.
 */
export async function prePopulate(
  req: PrePopulateRequest,
  baseUrl?: string,
): Promise<PrePopulatedConfigResponse> {
  return apiRequest<PrePopulatedConfigResponse>(
    ENDPOINTS.PRE_POPULATE,
    req,
    baseUrl,
  );
}

/**
 * Serialise all form section values into a YAML config string.
 */
export async function generateConfig(
  req: GenerateConfigRequest,
  baseUrl?: string,
): Promise<GenerateConfigResponse> {
  return apiRequest<GenerateConfigResponse>(
    ENDPOINTS.GENERATE,
    req,
    baseUrl,
  );
}

/**
 * Persist the generated YAML content to S3.
 */
export async function saveConfig(
  req: SaveConfigRequest,
  baseUrl?: string,
): Promise<SaveConfigResponse> {
  return apiRequest<SaveConfigResponse>(ENDPOINTS.SAVE, req, baseUrl);
}
